/**
 * Stable test ids for the AI Chat response surface.
 *
 * Single source of truth shared by the React source — which renders these as `data-testid` — and both test
 * runners, which query them via `getByTestId`. Because every side imports the same enum, renaming an id is a
 * compile error rather than a silent runtime mismatch.
 *
 * Scope: only the response surface, and only where ARIA is not already sufficient. Four widgets are
 * deliberately absent because they already carry stable, production-meaningful ARIA hooks that a test should
 * prefer — `role="table"` + `aria-label="Plan"` on the plan table, `role="group"` + `aria-label="Suggested
 * actions"` on a button group, `aria-expanded`/`aria-controls` on the draft-card toggle, and `role="alert"`
 * on the failed row itself. An id is added here only where the alternative is a hashed CSS-module class or an
 * English string, both of which break for reasons that have nothing to do with the behaviour under test.
 *
 * Convention matches the sibling registries: enum name is the feature, each member is the element, and the
 * value is SCREAMING_SNAKE_CASE carrying the `AICHAT` prefix so the id is self-describing in the DOM and in
 * test reports.
 */
export enum AiChatTestId {
  // --- Response rows -------------------------------------------------------
  /** The assistant reply row — text, images and widgets all render inside it. */
  ASSISTANT_MESSAGE_ROW = 'AICHAT_ASSISTANT_MESSAGE_ROW',
  /** The red failure row. Carries `role="alert"` too; the id is for locating it, the role for asserting semantics. */
  FAILED_MESSAGE_ROW = 'AICHAT_FAILED_MESSAGE_ROW',
  /** The failure row's Retry affordance. Absent whenever the row is not the last, or the chat is read-only. */
  FAILED_MESSAGE_RETRY = 'AICHAT_FAILED_MESSAGE_RETRY',

  // --- Loading states ------------------------------------------------------
  /**
   * The loading row. Present for BOTH loader shapes — the bare three-dots default and the cycling-text
   * variant — because they are the same row; which one renders is decided by whether loading texts exist.
   */
  PENDING_REPLY_LOADER = 'AICHAT_PENDING_REPLY_LOADER',
  /** The rotating status text. Absent on a default send, where the loading-texts array is empty. */
  CYCLING_LOADING_TEXT = 'AICHAT_CYCLING_LOADING_TEXT',
  /** The elapsed-turn counter. Renders only after 10s, so its absence before then is a state, not a failure. */
  ELAPSED_TURN_TIME = 'AICHAT_ELAPSED_TURN_TIME',

  // --- Generated result ----------------------------------------------------
  /** A model-generated image. Blurred while the turn is still streaming. */
  GENERATED_IMAGE = 'AICHAT_GENERATED_IMAGE',

  // --- Composer ------------------------------------------------------------
  /** The prompt textarea. */
  PROMPT_INPUT = 'AICHAT_PROMPT_INPUT',
  /** The send button. Never disabled for being out of credits — the wall is a response state, not an input one. */
  PROMPT_SUBMIT = 'AICHAT_PROMPT_SUBMIT',

  // --- Survey --------------------------------------------------------------
  /**
   * The survey's advance/submit control — "Next" on an intermediate step, and on the last step whatever
   * `spec.submitLabel` says, falling back to "Done". Neither text nor role pins it: the label is authored by
   * the model and translated.
   */
  SURVEY_SUBMIT = 'AICHAT_SURVEY_SUBMIT',

  // --- Credits and quota ---------------------------------------------------
  /** The credits pill in the account status bar. Reads the `stockmedia` number, not the team-credits slice. */
  CREDITS_PILL = 'AICHAT_CREDITS_PILL',
  /** The premium user's "Add more credits" CTA — opens the Stripe credit-pack dialog. */
  ADD_MORE_CREDITS_CTA = 'AICHAT_ADD_MORE_CREDITS_CTA',
  /** The non-premium "Upgrade for more credits" CTA — opens the premium pricing modal. */
  UPGRADE_FOR_CREDITS_CTA = 'AICHAT_UPGRADE_FOR_CREDITS_CTA',

  // --- Acting on a turn already sent ---------------------------------------
  /** Wraps a user's attachment together with the video strip made from it, so the video reads as that attachment's own. */
  USER_ATTACHMENT_WITH_VIDEO = 'AICHAT_USER_ATTACHMENT_WITH_VIDEO',
  /** The pencil on a user's own message. Revealed on hover, and inert while a turn is streaming. */
  USER_MESSAGE_EDIT = 'AICHAT_USER_MESSAGE_EDIT',
  /** The edit box's Send. Resends the edited prompt and truncates everything after it. */
  EDIT_MESSAGE_SEND = 'AICHAT_EDIT_MESSAGE_SEND',
  /** The per-chat options control in the history drawer — rename and delete live behind it. */
  CHAT_HISTORY_ITEM_OPTIONS = 'AICHAT_CHAT_HISTORY_ITEM_OPTIONS',
  /** An attached document's chip, in the prompt box and in the sent message alike. */
  DOCUMENT_CHIP = 'AICHAT_DOCUMENT_CHIP',
  SELECTION_CHIP = 'AICHAT_SELECTION_CHIP',

  // --- Brand kit swatches ------------------------------------------------
  /** One colour swatch in a brand-kit swatch cluster. The cluster is `aria-hidden` (its colours are named in text), so there is no role to query. */
  BRAND_KIT_COLOR_SWATCH = 'AICHAT_BRAND_KIT_COLOR_SWATCH',

  // --- Memory library ------------------------------------------------------
  /** One stored memory, fact or visual entity alike. Both tiers render the same card. */
  MEMORY_CARD = 'AICHAT_MEMORY_CARD',
  /** The per-memory options control on a card — delete lives behind it. */
  MEMORY_CARD_OPTIONS = 'AICHAT_MEMORY_CARD_OPTIONS',
  /** The Delete entry inside an open card menu. The menu portals out of the card, so the card is no handle for it. */
  MEMORY_CARD_DELETE = 'AICHAT_MEMORY_CARD_DELETE',
}
